import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { platformAPI } from '../../services/api';
import { LoadingSpinner } from '../../components/ui';
import {
  SlugBadge,
  TenantStatusBadge,
  UserActiveBadge,
  PlanTierBadge,
  OrganizationUsageBar,
  OrganizationPlanModal,
} from '../../components/platform';
import type { PlatformOrganization, SubscriptionTier } from '../../types/platformOrganization';

type TabKey = 'general' | 'admins' | 'audit';

const tabs: Array<{ id: TabKey; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'admins', label: 'Admins' },
  { id: 'audit', label: 'Auditoría' },
];

const PlatformTenantProfile: React.FC = () => {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [auditPage, setAuditPage] = useState(1);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const activeTab = (searchParams.get('tab') as TabKey) || 'general';

  const { data: profileRes, isLoading } = useQuery(
    ['platform-tenant-profile', tenantId],
    () => platformAPI.getTenantProfile(tenantId),
    { enabled: Boolean(tenantId) }
  );

  const { data: auditRes, isLoading: auditLoading } = useQuery(
    ['platform-tenant-audit', tenantId, auditPage],
    () => platformAPI.listTenantAudit(tenantId, { page: auditPage, limit: 15 }),
    { enabled: Boolean(tenantId) && activeTab === 'audit', keepPreviousData: true }
  );

  const updateOrg = useMutation(
    ({
      organizationId,
      body,
    }: {
      organizationId: string;
      body: { name?: string; tier?: SubscriptionTier };
    }) => platformAPI.updateOrganization(organizationId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-tenant-profile', tenantId]);
        queryClient.invalidateQueries(['platform-organizations']);
        queryClient.invalidateQueries(['platform-tenants']);
        toast.success('Plan actualizado');
        setPlanModalOpen(false);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } } };
        toast.error(e?.response?.data?.error || 'No se pudo actualizar el plan');
      },
    }
  );

  const profile = profileRes?.data?.data;
  const tenant = profile?.tenant;
  const subscription = profile?.subscription as PlatformOrganization | null | undefined;
  const admins = (profile?.admins || []) as Array<{
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    lastLogin: string | null;
  }>;
  const auditItems = (auditRes?.data?.items || []) as Array<{
    id: string;
    action: string;
    description: string;
    createdAt: string;
    user: { name: string; email: string } | null;
  }>;
  const auditPagination = auditRes?.data?.pagination as
    | { page: number; totalPages: number; total: number }
    | undefined;

  const accountKpis = useMemo(() => {
    if (!profile) return null;
    return [
      { label: 'Admins', value: admins.length },
      { label: 'Incidencias', value: profile.incidents?.total || 0 },
      { label: 'Incidencias abiertas', value: profile.incidents?.open || 0 },
      { label: 'Eventos auditoría', value: profile.audit?.total || 0 },
    ];
  }, [profile, admins.length]);

  if (isLoading || !profile || !tenant) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Perfil tenant</p>
            <h2 className="mt-1 text-2xl font-bold text-slate-900">{tenant.name}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <SlugBadge>{tenant.slug}</SlugBadge>
              <TenantStatusBadge isActive={tenant.isActive} />
              {subscription?.planLabel && (
                <PlanTierBadge tier={subscription.tier} label={subscription.planLabel} />
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {subscription && (
              <button
                type="button"
                onClick={() => setPlanModalOpen(true)}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
              >
                Cambiar plan
              </button>
            )}
            <Link
              to={`/platform/incidents?tenantId=${tenant.id}`}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Ver incidencias
            </Link>
            <Link
              to="/platform"
              className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Volver a funerarias
            </Link>
          </div>
        </div>
      </div>

      {subscription ? (
        <section className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Cuenta de facturación
              </p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">{subscription.organizationName}</h3>
              <p className="mt-1 text-sm text-slate-600">
                <span className="font-semibold text-slate-800">${subscription.priceUsd}/mes</span>
                {' · '}
                {subscription.tenantCount} funeraria{subscription.tenantCount !== 1 ? 's' : ''} en esta cuenta
              </p>
            </div>
            <Link
              to="/platform/plans"
              className="shrink-0 text-sm font-medium text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
            >
              Ver todas las cuentas →
            </Link>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="space-y-2.5 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Uso del plan</p>
              <OrganizationUsageBar
                label="Funerarias"
                used={subscription.usage.tenants}
                max={subscription.limits.maxTenants}
              />
              <OrganizationUsageBar
                label="Usuarios"
                used={subscription.usage.users}
                max={subscription.limits.maxUsers}
              />
              <OrganizationUsageBar
                label="Clientes"
                used={subscription.usage.activeClients}
                max={subscription.limits.maxActiveClients}
              />
              <OrganizationUsageBar
                label="Servicios este mes"
                used={subscription.usage.servicesThisMonth}
                max={subscription.limits.maxServicesPerMonth}
              />
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Funciones incluidas
              </p>
              <ul className="space-y-1 text-sm text-slate-700">
                <li>{subscription.features?.advancedReports ? '✓' : '✗'} Reportes avanzados</li>
                <li>{subscription.features?.emailAutomation ? '✓' : '✗'} Correos automáticos</li>
                <li>{subscription.features?.auditLog ? '✓' : '✗'} Auditoría</li>
                <li>{subscription.features?.apiAccess ? '✓' : '✗'} API</li>
              </ul>
              {subscription.tenants && subscription.tenants.length > 1 && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Otras funerarias en la cuenta
                  </p>
                  <ul className="space-y-1">
                    {subscription.tenants
                      .filter((t) => t.id !== tenant.id)
                      .map((t) => (
                        <li key={t.id}>
                          <Link
                            to={`/platform/tenants/${t.id}`}
                            className="text-sm font-medium text-slate-800 hover:underline"
                          >
                            {t.name}
                          </Link>
                          <span className="ml-2 text-xs text-slate-500">{t.slug}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900">
          Esta funeraria no tiene cuenta de facturación asignada.{' '}
          <Link to="/platform/plans" className="font-semibold underline">
            Configurar en Planes
          </Link>
        </section>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {accountKpis?.map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSearchParams({ tab: tab.id })}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white'
                    : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'general' && (
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Datos generales</p>
              <dl className="mt-2 space-y-2 text-sm">
                <div>
                  <dt className="text-slate-500">Contacto</dt>
                  <dd className="font-medium text-slate-900">{tenant.contactName || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Email</dt>
                  <dd className="font-medium text-slate-900">{tenant.contactEmail || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Teléfono</dt>
                  <dd className="font-medium text-slate-900">{tenant.phone || '—'}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estado operativo</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                <li>Branding: {profile.branding?.supported ? 'activo' : 'pendiente'}</li>
                <li>Módulos activos: {profile.modules?.supported ? 'preparado' : 'pendiente'}</li>
                <li>Estado de cuenta: {profile.accountStatus?.state || 'N/A'}</li>
                <li>Actividad reciente: pestaña auditoría</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="p-4">
            {admins.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
                No hay administradores registrados para esta funeraria.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2">Nombre</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Último acceso</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {admins.map((admin) => (
                      <tr key={admin.id}>
                        <td className="px-3 py-2 font-medium text-slate-900">{admin.name}</td>
                        <td className="px-3 py-2 text-slate-700">{admin.email}</td>
                        <td className="px-3 py-2">
                          <UserActiveBadge isActive={admin.isActive} />
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {admin.lastLogin
                            ? new Date(admin.lastLogin).toLocaleString('es')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="p-4">
            {auditLoading ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : auditItems.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
                No hay eventos de auditoría para esta funeraria.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2">Fecha</th>
                        <th className="px-3 py-2">Actor</th>
                        <th className="px-3 py-2">Acción</th>
                        <th className="px-3 py-2">Descripción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {auditItems.map((row) => (
                        <tr key={row.id}>
                          <td className="px-3 py-2 text-slate-600">
                            {new Date(row.createdAt).toLocaleString('es')}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.user?.name || 'Sistema'}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.action}</td>
                          <td className="px-3 py-2 text-slate-800">{row.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {auditPagination && auditPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>
                      Página {auditPagination.page} de {auditPagination.totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={auditPage <= 1}
                        onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                        className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
                      >
                        Anterior
                      </button>
                      <button
                        type="button"
                        disabled={auditPage >= auditPagination.totalPages}
                        onClick={() => setAuditPage((p) => p + 1)}
                        className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {subscription && (
        <OrganizationPlanModal
          isOpen={planModalOpen}
          mode="edit"
          organization={subscription}
          onClose={() => setPlanModalOpen(false)}
          onSubmit={(payload) =>
            updateOrg.mutate({
              organizationId: subscription.organizationId,
              body: payload,
            })
          }
          isLoading={updateOrg.isLoading}
        />
      )}
    </div>
  );
};

export default PlatformTenantProfile;
